import styled, { DefaultTheme } from 'styled-components';
import { Button, Select, Modal, Typography } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px;
  transition: background 0.3s ease;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')} !important;
  font-weight: 700 !important;
  font-size: 24px !important;

  @media (min-width: 768px) {
    font-size: 28px !important;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

export const FilterItem = styled.div`
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  max-width: 300px;

  .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
  }

  .ant-select-selection-item {
    line-height: 38px !important;
  }
`;

export const EmptyText = styled.div`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')};
  font-size: 14px;
  padding: 8px 0;
`;

export const TableWrapper = styled.div`
  margin-top: 16px;
  width: 100%;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  border-top-right-radius: 8px !important;
  border-top-left-radius: 8px !important;
  padding: 12px;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 8px rgba(0, 0, 0, 0.3)',
      '0 2px 8px rgba(0, 0, 0, 0.1)'
    )};
  
  &.no-data {
    overflow: hidden;
    
    .ant-table-container {
      overflow: hidden !important;
    }
    
    .ant-table-body {
      overflow: hidden !important;
    }
  }
`;

export const LogLink = styled.span`
  color: ${(props) => getThemeValue(props, '#60a5fa', '#2D6CCA')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) => getThemeValue(props, '#93c5fd', '#2560b8')};
    text-decoration: underline;
  }
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#2D3748', '#ffffff')};
    border-radius: 12px;
  }

  .ant-modal-header {
    background: ${(props) => getThemeValue(props, '#2D3748', '#ffffff')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }

  .ant-modal-body {
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }

  .ant-modal-close {
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ModalLabel = styled.div`
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  font-size: 14px;
`;

export const ModalValue = styled.div`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')};
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const ModalLogValue = styled(ModalValue)`
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#f5f5f5')};
  border-radius: 6px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => getThemeValue(props, '#2d3748', '#e5e7eb')};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => getThemeValue(props, '#4a5568', '#bfbfbf')};
    border-radius: 4px;

    &:hover {
      background: ${(props) => getThemeValue(props, '#718096', '#999999')};
    }
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
`;

export const StyledButton = styled(Button)`
  background-color: #2D6CCA !important;
  color: white !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  border: none !important;
  height: 40px;
  padding: 0 24px;
  
  &:hover {
    background-color: #2560b8 !important;
    color: white !important;
  }
  
  &:focus {
    background-color: #2D6CCA !important;
    color: white !important;
  }
`;

export const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px 16px;
`;

export const ErrorContainer = styled.div`
  text-align: center;
  padding: 40px 16px;
`;

export const ErrorText = styled.div`
  color: #ff4d4f;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 16px;
`;
